import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TermLabel } from "../../../api/content/ontologyApi";
import type { ConflictCheck } from "../../../api/content/reviewDecoders";
import { orderedLabels } from "../../ontology/viewer/ontologyModel";

const NO_NAMES: Readonly<Record<string, string>> = {};

/** A term's name among its labels: the reader's language first, then English, then any (`orderedLabels`); `undefined` without a label. */
export function labelIn(labels: readonly TermLabel[], language: string): string | undefined {
  return orderedLabels(labels, language)[0]?.value;
}

/** The changed terms' names in the reader's language, by IRI (R3-1). */
export function conflictTermNames(check: ConflictCheck | null, language: string): Readonly<Record<string, string>> {
  if (check === null) return NO_NAMES;
  return Object.fromEntries(Object.entries(check.termLabels).flatMap(([iri, labels]) => {
    const name = labelIn(labels, language);
    return name === undefined ? [] : [[iri, name]];
  }));
}

/** `conflictTermNames` in the app's language, again when it changes. */
export function useConflictTermNames(check: ConflictCheck | null): Readonly<Record<string, string>> {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "en";
  return useMemo(() => conflictTermNames(check, language), [check, language]);
}
